import WMTS from 'ol/source/WMTS';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { getWidth as getWidthOfExtent, getTopLeft as getTopLeftOfExtent } from 'ol/extent';
import {
  DEFAULT_STYLE, DEFAULT_FORMAT, DEFAULT_LIGHT_CONDITION, LIGHT_CONDITION_PARAM,
} from './GeomodSourceUtils';
import { TileLayerCacheStrategy } from '../utils/cache';
// import { updateQueryString, tileLoader } from '../utils/httpRequest';
import { updateQueryString } from '../utils/httpRequest';
import { LAYERS_PARAMETERS_UPDATED_EVENT_TYPE } from '../utils/events.js';

/**
 * Create the WMTS layer tide grid.
 * @param {Array<number>} projectionExtent The projection extent.
 * @return {WMTSTileGrid} The created tile grid.
 */
function createTileGrid(projectionExtent) {
  const size = getWidthOfExtent(projectionExtent) / 256;
  const resolutions = new Array(22);
  const matrixIds = new Array(22);
  for (let z = 0; z < 22; ++z) {
    // generate resolutions and matrixIds arrays for this WMTS
    resolutions[z] = size / 2 ** z;
    matrixIds[z] = z;
  }
  return new WMTSTileGrid({
    origin: getTopLeftOfExtent(projectionExtent),
    resolutions,
    matrixIds,
  });
}

/**
 * @typedef {Object} GeomodWMTSOptions
 * @property {string} name The name of the layer.
 * @property {string} url The layer URL.
 * @property {import('ol/proj/Projection').default} projection The projection.
 * @property {string} [format] The new default format is image/png.
 * @property {string} [style] The new default style is default.
 * @property {TileLayerCacheStrategyOptions} [cache]  Cache strategy settings
 * @property {Map<string,string>} [extraParameters] The extra parameters for the request exemple: AIS_SHOW_TRACE=false.
 */

/**
 * The Geomod WMS tile source.
 * @extends {WMTS}
 */
export default class GeomodWMTS extends WMTS {
  /**
     * @constructor
     * @param {GeomodWMTSOptions} options The options.
     */
  constructor(options = {}) {
    let extraParametersOptions;
    if (options.extraParameters) {
      extraParametersOptions = {};
      const extraParameters = JSON.parse(localStorage.getItem('layerExtraParameters')) || {};
      for (const key of options.extraParameters) {
        extraParametersOptions[key] = extraParameters[key];
      }
    }

    super({
      interpolate: false,
      url: options.url,
      tileGrid: createTileGrid(options.projection.getExtent()),
      matrixSet: options.matrixSet || options.projection.getCode(),
      layer: options.name,
      format: options.format || DEFAULT_FORMAT,
      style: options.style || DEFAULT_STYLE,
      // tileLoadFunction: tileLoader
    });

    /**
         * The layer extra parameters.
         * @type {Object<string,string>}
         */
    this.params_ = {
      ...extraParametersOptions,
      [LIGHT_CONDITION_PARAM]: options.light || DEFAULT_LIGHT_CONDITION,
      token: localStorage.getItem('token')
    };

    /**
         * The original urls
         * @type {Array<string>}
         */
    this.originalUrls_ = this.getUrls();

    /**
         * The source tag
         * @private
         * @type {string}
         */
    this.tag_ = options.filter;

    /**
         * The cache strategy used by the layer
         * @type {TileLayerCacheStrategy}
         */
    this.cacheStrategy = new TileLayerCacheStrategy(options.cache);

    document.addEventListener(LAYERS_PARAMETERS_UPDATED_EVENT_TYPE, () => {
      this.cacheStrategy.run(this);
    });
  }

  /**
     * Get the source tag.
     * @return {string} The source tag.
     */
  getTag() {
    return this.tag_;
  }

  /**
     * Get the user-provided params, i.e. those passed to the constructor through
     * the "params" option, and possibly updated using the updateParams method.
     * @return {Object} Params.
     */
  getParams() {
    return this.params_;
  }

  /**
     * Update the user-provided params.
     * @param {Object} params Params.
     */
  updateParams(params) {
    this.params_ = { ...this.params_, ...params };
    const urls = this.originalUrls_.map((url) => updateQueryString(url, this.params_));
    this.setUrls(urls);
    this.refresh();
  }
}
